import React, { useState, useMemo } from 'react'
import { Box, Typography, Button } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import { FAQData, FAQResponse, WalletFAQCollection } from '@/src/types/walletFaq'

export interface WalletFAQProps {
    initialFAQs?: WalletFAQCollection | null
    isDesktop?: boolean
    isCleartripBrand?: boolean
    isMultiBrand?: boolean
}

const WalletFAQ: React.FC<WalletFAQProps> = ({
    initialFAQs,
    isDesktop = false,
    isCleartripBrand = false,
    isMultiBrand = false
}) => {
    const locale = useLocale() as 'en' | 'ar'
    const tCommon = useTranslations('common')
    const t = useTranslations('wallet')
    const [activeBrand, setActiveBrand] = useState<'MAIN' | 'LINKED'>('MAIN')
    const effectiveFlyinFaqResponse = initialFAQs?.flyin || null
    const effectiveCleartripFaqResponse = initialFAQs?.cleartrip || null

    const getFaqByLocale = (faqResponse: FAQResponse | null | undefined): FAQData | null => {
        if (!faqResponse?.faq_data) return null
        return faqResponse.faq_data[locale] || faqResponse.faq_data.en
    }

    const hasAnyFaqData = isCleartripBrand
        ? !!effectiveCleartripFaqResponse
        : !!effectiveFlyinFaqResponse || !!effectiveCleartripFaqResponse
    const availableBrands: Array<'MAIN' | 'LINKED'> = isCleartripBrand
        ? [...(effectiveCleartripFaqResponse ? (['LINKED'] as const) : [])]
        : isMultiBrand
          ? [
                ...(effectiveFlyinFaqResponse ? (['MAIN'] as const) : []),
                ...(effectiveCleartripFaqResponse ? (['LINKED'] as const) : [])
            ]
          : [...(effectiveFlyinFaqResponse ? (['MAIN'] as const) : [])]

    const activeBrandWithFallback = isCleartripBrand
        ? 'LINKED'
        : activeBrand === 'MAIN' && !effectiveFlyinFaqResponse && effectiveCleartripFaqResponse
          ? 'LINKED'
          : activeBrand === 'LINKED' && !effectiveCleartripFaqResponse && effectiveFlyinFaqResponse
            ? 'MAIN'
            : activeBrand

    const displayedFaqData = useMemo(() => {
        return activeBrandWithFallback === 'MAIN'
            ? getFaqByLocale(effectiveFlyinFaqResponse)
            : getFaqByLocale(effectiveCleartripFaqResponse)
    }, [activeBrandWithFallback, effectiveFlyinFaqResponse, effectiveCleartripFaqResponse, locale])

    if (!hasAnyFaqData || !displayedFaqData || displayedFaqData.questions.length === 0) {
        return null
    }

    return (
        <Box sx={{ bgcolor: 'background.paper' }}>
            <Box sx={{ px: isDesktop ? 2 : 2, py: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {tCommon('termsAndConditions')}
                </Typography>
                {!isCleartripBrand && isMultiBrand && availableBrands.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                        <Button
                            variant={activeBrandWithFallback === 'MAIN' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setActiveBrand('MAIN')}
                            sx={{
                                borderRadius: 5,
                                px: 2,
                                py: 0.75,
                                textTransform: 'none',
                                fontWeight: activeBrandWithFallback === 'MAIN' ? 600 : 500,
                                fontSize: '0.8125rem'
                            }}
                        >
                            {t('brandFilters.flyinWallet')}
                        </Button>
                        <Button
                            variant={activeBrandWithFallback === 'LINKED' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setActiveBrand('LINKED')}
                            sx={{
                                borderRadius: 5,
                                px: 2,
                                py: 0.75,
                                textTransform: 'none',
                                fontWeight: activeBrandWithFallback === 'LINKED' ? 600 : 500,
                                fontSize: '0.8125rem'
                            }}
                        >
                            {t('brandFilters.cleartripWallet')}
                        </Button>
                    </Box>
                )}

                {displayedFaqData.questions.map((item, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 0.75
                            }}
                        >
                            {item.question}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.8,
                                whiteSpace: 'pre-line'
                            }}
                        >
                            {item.answer}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export { WalletFAQ }
export default WalletFAQ
